// ============================================
// TEMPS RÉEL — traitement des événements du serveur
// ============================================
// Contrat :
// - `dossier:modifie` { ticketId, motif, a } est émis vers tout le personnel :
//   la liste et le tableau de bord se rechargent, la fiche ouverte sur ce
//   dossier se relit ;
// - dans la salle d'un dossier (join:ticket) : `message:new` (le message
//   lui-même, sans ticketId) et `ticket:updated` { ticketId, field, value }.
// Fonctions pures : l'écran ne fait que brancher le résultat.

import type { Ticket, TicketMessage, User, Attachment } from '../../types';

export type MotifModification = 'cree' | 'modifie' | 'message' | 'email' | 'fusion';

export interface DossierModifie {
  ticketId: string;
  motif: MotifModification;
  a?: string;
}

/** Délai de regroupement des rechargements (une rafale d'événements → une relecture). */
export const DELAI_REGROUPEMENT_MS = 1500;

const MOTIFS: MotifModification[] = ['cree', 'modifie', 'message', 'email', 'fusion'];

/** Valide la charge utile ; null si elle est inexploitable. Un motif inconnu vaut « modifie ». */
export function lireDossierModifie(data: unknown): DossierModifie | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.ticketId !== 'string' || !d.ticketId) return null;
  const motif = MOTIFS.includes(d.motif as MotifModification) ? (d.motif as MotifModification) : 'modifie';
  return { ticketId: d.ticketId, motif, a: typeof d.a === 'string' ? d.a : undefined };
}

/**
 * Fenêtre de rechargement des dossiers sans agent (lecture plus coûteuse :
 * plusieurs pages de dossiers). Au plus une lecture par fenêtre et par poste.
 */
export const DELAI_NON_ASSIGNES_MS = 30_000;

/** Motifs qui peuvent changer l'ensemble des dossiers sans agent. */
const MOTIFS_NON_ASSIGNES: MotifModification[] = ['cree', 'modifie', 'fusion'];

/**
 * Que faire à la réception de `dossier:modifie` ?
 * - toujours recharger la liste et le tableau de bord (page et statistiques) ;
 * - ne relire les dossiers sans agent que si le motif peut les changer
 *   (création, modification, fusion), pas pour un message ou un e-mail ;
 * - relire la fiche si elle est ouverte sur ce dossier.
 */
export function decisionDossierModifie(
  data: unknown,
  ficheOuverteId: string | null | undefined,
): { rechargerListes: boolean; rechargerNonAssignes: boolean; relireFiche: boolean } {
  const evt = lireDossierModifie(data);
  if (!evt) return { rechargerListes: false, rechargerNonAssignes: false, relireFiche: false };
  return {
    rechargerListes: true,
    rechargerNonAssignes: MOTIFS_NON_ASSIGNES.includes(evt.motif),
    relireFiche: !!ficheOuverteId && evt.ticketId === ficheOuverteId,
  };
}

/**
 * Limiteur : le premier déclenchement agit tout de suite ; ceux qui arrivent
 * pendant la fenêtre qui suit sont regroupés en une seule action à la fin de
 * la fenêtre. Au plus une action par fenêtre, sans perdre le dernier changement.
 */
export function creerLimiteur(delaiMs: number, action: () => void): { declencher: () => void; annuler: () => void } {
  let minuteur: ReturnType<typeof setTimeout> | null = null;
  let enAttente = false;
  const ouvrirFenetre = () => {
    minuteur = setTimeout(() => {
      minuteur = null;
      if (enAttente) {
        enAttente = false;
        action();
        ouvrirFenetre();
      }
    }, delaiMs);
  };
  return {
    declencher() {
      if (minuteur !== null) { enAttente = true; return; }
      action();
      ouvrirFenetre();
    },
    annuler() {
      if (minuteur !== null) clearTimeout(minuteur);
      minuteur = null;
      enAttente = false;
    },
  };
}

/**
 * Regroupe les déclenchements : le premier programme l'action après `delaiMs`,
 * les suivants pendant l'attente sont absorbés. L'action s'exécute donc au
 * plus une fois par fenêtre, même sous une rafale continue.
 */
export function creerRegroupeur(delaiMs: number, action: () => void): { declencher: () => void; annuler: () => void } {
  let minuteur: ReturnType<typeof setTimeout> | null = null;
  return {
    declencher() {
      if (minuteur !== null) return;
      minuteur = setTimeout(() => {
        minuteur = null;
        action();
      }, delaiMs);
    },
    annuler() {
      if (minuteur !== null) clearTimeout(minuteur);
      minuteur = null;
    },
  };
}

/**
 * Ajoute un message sans doublon (même id). Si le message est déjà présent,
 * la version la plus complète l'emporte : celle qui porte le rôle de
 * l'auteur (réponse de l'API) remplace celle reçue par le socket. Rend le
 * même tableau quand rien ne change.
 */
export function ajouterMessageSansDoublon(messages: TicketMessage[], nouveau: TicketMessage): TicketMessage[] {
  const i = messages.findIndex(m => m.id === nouveau.id);
  if (i === -1) return [...messages, nouveau];
  const existant = messages[i];
  if (!existant.author?.role && nouveau.author?.role) {
    const copie = messages.slice();
    copie[i] = { ...existant, ...nouveau };
    return copie;
  }
  return messages;
}

/**
 * Convertit la charge utile de `message:new` en message de la fiche. Le
 * serveur n'envoie que `authorId` et `authorName` : l'auteur est reconnu
 * comme client s'il est le titulaire du dossier.
 */
export function messageDepuisSocket(data: unknown, ticketId: string, customerId?: string | null): TicketMessage | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.id !== 'string' || typeof d.content !== 'string') return null;
  // Un message qui annonce un autre dossier n'est pas pour cette fiche.
  if (typeof d.ticketId === 'string' && d.ticketId !== ticketId) return null;
  const authorId = typeof d.authorId === 'string' ? d.authorId : '';
  const authorName = typeof d.authorName === 'string' ? d.authorName : undefined;
  const estClient = !!authorId && !!customerId && authorId === customerId;
  const author: User | undefined = authorId || authorName
    ? {
        id: authorId,
        email: '',
        displayName: authorName || (d.isAI ? 'Assistant IA' : ''),
        role: estClient ? 'CUSTOMER' : '',
        createdAt: '',
      }
    : undefined;
  const createdAt = typeof d.createdAt === 'string' ? d.createdAt : new Date().toISOString();
  return {
    id: d.id,
    ticketId,
    authorId,
    author,
    content: d.content,
    isInternal: d.isInternal === true,
    attachments: Array.isArray(d.attachments) ? (d.attachments as Attachment[]) : [],
    createdAt: createdAt as unknown as Date,
    // Message de l'assistant automatique : affiché comme tel, pas comme un conseiller.
    ...(d.isAI === true ? { estAssistant: true } : {}),
  };
}

/**
 * Applique `ticket:updated` { ticketId, field, value } au dossier affiché.
 * Rend le même objet si l'événement concerne un autre dossier ou est mal formé.
 */
export function fusionnerChampDossier<T extends Ticket>(ticket: T, data: unknown): T {
  if (!data || typeof data !== 'object') return ticket;
  const d = data as Record<string, unknown>;
  if (d.ticketId !== ticket.id || typeof d.field !== 'string' || !d.field) return ticket;
  // Champs techniques jamais écrasés par un événement.
  if (d.field === 'id' || d.field === '__proto__' || d.field === 'constructor') return ticket;
  if ((ticket as unknown as Record<string, unknown>)[d.field] === d.value) return ticket;
  return { ...ticket, [d.field]: d.value };
}
